import { useState, useCallback, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import OrellaChatAgent from './agent/OrellaChatAgent'
import { AgentProvider } from './AgentContext'
import { useProfileStore, useCaseStore, useUserStore } from '../stores'
import { buildProfileSummary } from '../utils/profileSummary'
import { planFollowUp } from '../utils/signedPlan' // PROD-1079: no draft plan reaches the agent
import { useI18n } from '../i18n'

/**
 * GlobalAgentProvider — v223.4.0 (Phase 2a agent consolidation)
 *
 * Two conversation scopes instead of seven:
 *   - master : ONE patient-level thread for every non-case screen (home, cases,
 *              visits, profile, doctors). Its greeting reflects the current
 *              screen; it never carries a specific case as its subject.
 *   - case   : per-case thread (keyed by case UUID) carrying full case context,
 *              enriched with materials accumulated across the case's encounters.
 *
 * Provides the FAB, a single bottom-sheet OrellaChatAgent, and AgentContext.
 */

// v229.1.1: The floating "Ask Orella" FAB has been removed app-wide — every
// screen now hosts an inline Orella entry (home hero, case home, focus pages,
// summary, etc.), so the floater was redundant. The route lists that gated the
// FAB's visibility are gone with it. suppressFab / setFabDialogue remain in the
// context as harmless no-ops so existing callers keep working.

// Phase (case scope only) — drives the case greeting.
function routeToPhase(pathname) {
  if (pathname.startsWith('/case/describe')) return 2
  if (pathname.startsWith('/case/detect')) return 3
  if (pathname.startsWith('/case/share') || pathname.startsWith('/case/diagnose')) return 4
  if (pathname.startsWith('/case/deliver')) return 6
  return null
}

function formatSpecialtyName(id) {
  if (!id) return ''
  return id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Which non-case screen are we on? Drives the master greeting and per-turn
// awareness sent to the agent.
function screenForPath(pathname, agentSource) {
  if (pathname.startsWith('/case/')) return 'case'
  if (pathname.startsWith('/profile')) return 'profile'
  if (pathname.startsWith('/cases')) return 'cases'
  if (pathname.startsWith('/visits')) return 'visits'
  if (pathname.startsWith('/doctors') || agentSource === 'doctors_screen') return 'doctors'
  return 'home'
}

export default function GlobalAgentProvider({ children }) {
  const [agentOpen, setAgentOpen] = useState(false)
  const [pendingMessage, setPendingMessage] = useState(null)
  const [agentSource, setAgentSource] = useState(null)
  const [fabDialogue, setFabDialogue] = useState(null)
  const [fabSuppressed, setFabSuppressed] = useState(false)
  // Home-chat session flag — drives the HomeScreen "continue our chat" pill.
  // The thread itself is the unified master scope; this only controls home UI.
  const [homeSessionActive, setHomeSessionActive] = useState(() => {
    try { return !!sessionStorage.getItem('home_chat_session') } catch { return false }
  })
  const location = useLocation()
  const navigate = useNavigate()
  const { language, t } = useI18n()
  const user = useUserStore((s) => s.user)
  const isLocked = useUserStore((s) => s.isLocked)
  const profile = useProfileStore((s) => s.profile)
  const currentCase = useCaseStore((s) => s.currentCase)
  const allCases = useCaseStore((s) => s.cases)

  const firstName = user?.firstName || profile?.firstName || ''
  const pathname = location.pathname

  // Clear session + close agent on logout (user becomes null). Previously the
  // chatbot could stay open after a session timeout and silently 401.
  useEffect(() => {
    if (!user) {
      try { sessionStorage.removeItem('home_chat_session') } catch {}
      setHomeSessionActive(false)
      setAgentOpen(false)
      setFabDialogue(null)
      setPendingMessage(null)
    }
  }, [user])

  const screen = useMemo(() => screenForPath(pathname, agentSource), [pathname, agentSource])
  const isCaseScope = pathname.startsWith('/case/') && !!currentCase?.id

  // Reference-only list of active cases (for the master thread). This lets the
  // agent point a patient toward a case without adopting one as the subject.
  const caseList = useMemo(() => {
    if (!allCases || allCases.length === 0) return []
    return allCases
      .filter(c => c.status !== 'completed' && c.status !== 'closed')
      .slice(0, 8)
      .map(c => ({
        id: c.id,
        specialty: c.specialtyName || c.specialty || formatSpecialtyName(c.specialtyId) || '',
        focusArea: c.focusAreaName || c.focusArea || c.focus_area || '',
        status: c.status || 'draft',
        doctorName: c.doctorName || c.doctor_name || '',
      }))
  }, [allCases])

  // Context sent to /agent/chat. Master scope intentionally omits any specific
  // case so the agent can't dive into a stale one (the F3 leak fix). Case scope
  // carries full case context plus materials accumulated across encounters.
  const caseContext = useMemo(() => {
    const base = {
      scope: isCaseScope ? 'case' : 'master',
      screen,
      language,
      firstName,
      profileSummary: buildProfileSummary(profile),
    }
    if (agentSource) base.openedFrom = agentSource

    if (!isCaseScope) {
      base.caseList = caseList.length > 0 ? caseList : undefined
      return base
    }

    const phase = routeToPhase(pathname)
    // ★★★ v242.122.0 · DELIVER-AGENT-OPENER-1 — Dr. Rami: "need to fix the chat prompt
    //   for the deliver page ... it should read 'Hi [name] ask me anything about your
    //   doctors treatment and follow up plan'."
    //   ★★★ THE LINE HE SAW WAS CASE HOME'S OPENER, SEEDED INTO THE STORED THREAD —
    //     "Check Deliver to complete your pre-visit checklist" — which is a nudge to go
    //     to a screen the patient is already standing on. It persisted because the
    //     thread is per-case and its first line is frozen at creation.
    //   ★★ THE MECHANISM TO FIX IT ALREADY EXISTS AND IS NOT A NEW ONE: useAgentStore
    //     takes `caseContext.opener` as the greeting override, and re-seeds a thread
    //     whose opener changed WHILE IT IS STILL JUST THE GREETING. Once the patient has
    //     said anything the thread is theirs and nothing rewrites its first line.
    //   ★★★ v242.125.0 · DELIVER-AGENT-OPENER-2 — `&& agentOpen` IS THE WHOLE FIX.
    //
    //   v242.122.0 gave this provider an opener. It is the SECOND agent that can
    //   claim one: CaseHome renders its own inline OrellaChatAgent with the SAME
    //   caseId and its own opener (`caseHome.chatFollowUpPreVisit`). Two mounted
    //   agents, one thread, two different openers.
    //
    //   They are both mounted at once for ~400ms on every /case/home -> /case/deliver
    //   move, because <Routes> sits inside <AnimatePresence>: the exiting page stays
    //   mounted for its exit animation while `pathname` is ALREADY /case/deliver. So
    //   the provider says "the opener is the plan line" and CaseHome says "the opener
    //   is the checklist line" — in the same commit.
    //
    //   useAgentStore.ensureConversation() re-seeds a still-unsent thread when the
    //   opener changes. ★ v242.157.0 — IT USED TO BE CALLED `getConversation` AND TO
    //   DO THAT WITH A set() DURING RENDER (AGENT-RENDER-PHASE-SET-1); it is now
    //   called from a layout effect and the name says it writes. The hazard this
    //   paragraph describes is what made two openers disagree, and that half stands. Since v242.101.0 this
    //   component subscribes to `conversations` (the pendingActions selector), so that
    //   write synchronously notifies React mid-render. A re-seeds -> B re-renders ->
    //   B re-seeds -> A re-renders, unbounded, inside React's commit flush. That is
    //   the tab lock Dr. Rami saw on #13AE1F12, and reverting v242.123.0 could not
    //   touch it because the cause shipped in 122.
    //
    //   A CLOSED BOTTOM SHEET HAS NO BUSINESS REWRITING A THREAD'S FIRST LINE. The
    //   opener only matters when this sheet is the surface the patient is reading,
    //   and the sheet is never open mid-transition — so the two claims can no longer
    //   overlap. v242.122.0's intent is preserved exactly: open Ask Orella on Deliver
    //   and the thread opens on the plan.
    const deliverOpener = (phase === 6 && agentOpen)
      ? (firstName ? t('agent.deliverOpenerNamed', { name: firstName }) : t('agent.deliverOpener'))
      : undefined
    const isExternalPath = currentCase?.doctorSource === 'external' || currentCase?.doctor_source === 'external'
    const otherActiveCases = caseList.filter(c => c.id !== currentCase?.id).slice(0, 5)
    return {
      ...base,
      specialty: currentCase?.specialtyId ? formatSpecialtyName(currentCase.specialtyId) : '',
      focusArea: currentCase?.focusAreaId || currentCase?.focusArea || '',
      phase: phase ?? 2,
      ...(deliverOpener ? { opener: deliverOpener } : {}),
      status: currentCase?.status || 'draft',
      doctorSource: currentCase?.doctorSource || currentCase?.doctor_source || '',
      isExternalPath,
      voiceSummary: currentCase?.describe?.voiceTranscript || '',
      diagnosis: currentCase?.diagnose?.diagnosis || currentCase?.diagnosis || '',
      doctorName: currentCase?.doctorName || currentCase?.doctor_name || '',
      otherActiveCases: otherActiveCases.length > 0 ? otherActiveCases : undefined,
      // Encounter enrichment — materials accumulated within the case so the
      // thread gets richer as encounters complete (per-case, not per-encounter).
      caseMaterials: {
        hasConsultNote: !!(currentCase?.consultNote || currentCase?.diagnose?.consultNote),
        hasAfterVisitSummary: !!(currentCase?.avsContent || currentCase?.afterVisitSummary || currentCase?.diagnose?.avsContent),
        diagnosis: currentCase?.diagnose?.diagnosis || currentCase?.diagnosis || '',
        status: currentCase?.status || 'draft',
        followUp: currentCase ? planFollowUp(currentCase) : undefined, // PROD-1079: md's draft plan keys dropped until signed
      },
    }
  }, [pathname, isCaseScope, screen, language, firstName, profile, agentSource, caseList, currentCase, agentOpen])

  // Two scopes: the case UUID on case pages, otherwise the single master thread.
  const caseId = useMemo(() => {
    if (isCaseScope) return currentCase.id
    return 'master'
  }, [isCaseScope, currentCase?.id])

  // ★★★ v242.173.0 · BUILD123-REINSTATE-1 (1 of 3) — LOCKING THE SESSION CLOSES THE AGENT.
  //   Reinstated from v242.123.0, withdrawn whole during the DELIVER-AGENT-OPENER-2 revert
  //   chain for a defect in a different file.
  //   ★★ RAISING THE LOCK ABOVE THE CHAT (utils/zLayers.js) only COVERS the sheet: it stays
  //   in the DOM, stays reachable by assistive tech, and is still there to flash back the
  //   instant the PIN is accepted. COVERING PHI IS NOT PUTTING IT AWAY, and this is the
  //   half that matters. The tier is the half that stops it being a race.
  useEffect(() => {
    if (isLocked) setAgentOpen(false)
  }, [isLocked])

  const handleOpen = useCallback((message, source) => {
    if (message) setPendingMessage(message)
    if (source) setAgentSource(source)
    setFabDialogue(null)
    setAgentOpen(true)
  }, [])

  // Home chat session — sets the flag the HomeScreen pill reads. Called on the
  // first send from the home hero.
  const startHomeSession = useCallback(() => {
    try { sessionStorage.setItem('home_chat_session', JSON.stringify({ startedAt: Date.now() })) } catch {}
    setHomeSessionActive(true)
  }, [])

  const clearHomeSession = useCallback(() => {
    try { sessionStorage.removeItem('home_chat_session') } catch {}
    setHomeSessionActive(false)
  }, [])

  const handleClose = useCallback(() => {
    setAgentOpen(false)
    setPendingMessage(null)
    setAgentSource(null)
  }, [])

  const handleInitialMessageConsumed = useCallback(() => {
    setPendingMessage(null)
  }, [])

  const suppressFab = useCallback((suppress) => {
    setFabSuppressed(suppress)
  }, [])

  // Fullscreen agent was never wired to any consumer; kept as graceful no-op
  // stubs so the context shape stays stable. Single bottom-sheet render below.
  const noop = useCallback(() => {}, [])

  return (
    <AgentProvider value={{
      openAgent: handleOpen,
      openAgentFullscreen: handleOpen,
      isAgentFullscreen: false,
      closeAgentFullscreen: noop,
      setFabDialogue,
      suppressFab,
      homeSessionActive,
      startHomeSession,
      clearHomeSession,
      // Live scope for screens that embed the agent inline (HomeScreen hero).
      // On /home this is the master/home thread; HomeScreen renders the inline
      // chat with this exact context so it matches the bottom-sheet's.
      agentCaseId: caseId,
      agentCaseContext: caseContext,
    }}>
      {children}
      <OrellaChatAgent
        isOpen={agentOpen}
        onClose={handleClose}
        caseId={caseId}
        caseContext={caseContext}
        onNavigate={navigate}
        initialMessage={pendingMessage}
        onInitialMessageConsumed={handleInitialMessageConsumed}
      />
    </AgentProvider>
  )
}
